import type { GradedWork } from '../types';

export function escapeHtmlForText(s: string): string {
  return (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Allow only safe image URLs for print HTML (http(s) or data:image). */
export function safeFeedbackImageSrc(url: string): string | null {
  const u = (url ?? '').trim();
  if (u.startsWith('https://') || u.startsWith('http://')) return u;
  if (u.startsWith('data:image/')) return u;
  return null;
}

export function buildFeedbackBatchExportHtml(
  works: GradedWork[],
  meta: { title?: string; subtitle?: string; generatedAtMs: number }
): string {
  const title = meta.title ?? 'DoneGrading — feedback export';
  const subtitle = (meta.subtitle ?? '').trim();
  const when = new Date(meta.generatedAtMs).toISOString();
  const rows = works
    .map((w, i) => {
      const safeName = escapeHtmlForText(w.studentName || 'Student');
      const safeFeedback = escapeHtmlForText((w.feedback || '').trim()).replace(/\n/g, '<br/>');
      const course = escapeHtmlForText(w.courseName || '');
      const asn = escapeHtmlForText(w.assignmentName || '');
      const status = escapeHtmlForText(w.status);
      const scoreN = typeof w.score === 'number' && !Number.isNaN(w.score) ? w.score : 0;
      const maxN = typeof w.maxScore === 'number' && !Number.isNaN(w.maxScore) ? w.maxScore : 0;
      const imgs = (w.imageUrls || [])
        .map((u) => safeFeedbackImageSrc(u))
        .filter((u): u is string => !!u)
        .slice(0, 4)
        .map((u) => `<div class="thumb"><img src="${u}" alt="" /></div>`)
        .join('');
      return `
    <section class="card">
      <h2>${i + 1}. ${safeName}</h2>
      <p class="meta">${course} · ${asn} · Score: ${scoreN} / ${maxN} · ${status}</p>
      <div class="feedback">${safeFeedback || '<em>No feedback</em>'}</div>
      ${imgs ? `<div class="thumbs">${imgs}</div>` : ''}
    </section>`;
    })
    .join('');

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${escapeHtmlForText(title)}</title>
  <style>
    body{font-family:system-ui,-apple-system,sans-serif;margin:1rem;color:#111;line-height:1.4}
    h1{font-size:1.25rem;margin:0 0 .25rem}
    .sub{color:#555;font-size:.85rem;margin:0 0 1rem}
    .card{page-break-inside:avoid;page-break-after:always;border:1px solid #ddd;border-radius:8px;padding:1rem;margin-bottom:1rem}
    .card:last-of-type{page-break-after:auto}
    h2{font-size:1.05rem;margin:0 0 .35rem}
    .meta{font-size:.78rem;color:#444;margin:0 0 .65rem}
    .feedback{font-size:.95rem}
    .thumbs{display:flex;gap:8px;margin-top:10px;flex-wrap:wrap}
    .thumb img{max-height:120px;max-width:160px;object-fit:contain;border:1px solid #eee}
    @media print{body{margin:.4in}}
  </style></head><body>
  <h1>${escapeHtmlForText(title)}</h1>
  <p class="sub">${escapeHtmlForText(subtitle)}${subtitle ? ' · ' : ''}Generated ${when} · ${works.length} submission(s)</p>
  ${rows || '<p>No rows.</p>'}
  </body></html>`;
}

/** Opens print dialog; user can choose “Save as PDF” in the browser. */
export function openFeedbackBatchPrintWindow(html: string): boolean {
  const w = window.open('', '_blank', 'noopener,noreferrer');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  requestAnimationFrame(() => {
    try {
      w.print();
    } catch {
      /* ignore */
    }
  });
  return true;
}
